/**
 * Czech/English text for a plan-cap refusal (KAN-1665).
 *
 * The API sends ``plan_count_limit_reached`` / ``plan_seat_limit_reached``
 * / ``plan_operator_seat_limit_reached`` with ``params`` (resource,
 * limit, current, plan name). Its ``message`` is an English log line;
 * forms used to print it verbatim — "Plan free allows 5 materials …
 * increase the limit in /admin/plans", a path the tenant cannot open.
 *
 * :class:`ApiError` builds its message through this function, so every
 * form that shows ``err.message`` gets the translated sentence without
 * knowing about plan caps. Resource names live in one place
 * (``common:planLimits.resource.*``) — the plan page and the over-limit
 * notices read the same labels.
 *
 * Returns ``null`` for any other error, and for a resource without a
 * label (the customer-side variant cap has its own copy in the paywall
 * modal), so the caller keeps the server text.
 */

import i18next from "i18next";

const SEAT_RESOURCE: Record<string, string> = {
  plan_seat_limit_reached: "users",
  plan_operator_seat_limit_reached: "operator_users",
};

export function planLimitResourceLabel(resource: string): string | null {
  const key = `common:planLimits.resource.${resource}`;
  return i18next.exists(key) ? i18next.t(key) : null;
}

export function planLimitMessage(
  code: string,
  params: Record<string, unknown>,
): string | null {
  // KAN-1674 — měsíční strop přijatých poptávek odmítá ZÁKAZNÍKA obchodu
  // (formulář, poptávka z košíku). O tarifu obchodu mu nic neříkáme.
  if (code === "plan_monthly_limit_reached") {
    return i18next.t("common:planLimits.monthlyRefusedCustomer");
  }
  const resource =
    code === "plan_count_limit_reached"
      ? typeof params.resource === "string"
        ? params.resource
        : null
      : (SEAT_RESOURCE[code] ?? null);
  if (resource === null) return null;
  const label = planLimitResourceLabel(resource);
  if (label === null) return null;
  return i18next.t("common:planLimits.refused", {
    resource: label,
    limit: params.limit,
    current: params.current,
    plan: params.plan_name ?? params.plan_tier ?? "",
  });
}
